import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  Modal,
  CircularProgress,
} from "@mui/material";
import { InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";

import md5 from "blueimp-md5";

import PersonIcon from "@mui/icons-material/Person";
import HttpsIcon from "@mui/icons-material/Https";

import { motion } from "framer-motion";
import axios from "axios";

// --- 🧠 MINI NEURAL COMPONENT (SPECIFIC FOR CIRCLES) ---
const MiniNeuralNetwork = ({
  nodeColor = "#ffffff",
  signalColor = "#ffffff",
  lineColor = "rgba(255, 255, 255, 0.4)",
}) => {
  // Nodes optimized for a smaller circular view
  const nodes = [
    { x: 50, y: 40 },
    { x: 150, y: 80 },
    { x: 250, y: 40 },
    { x: 20, y: 120 },
    { x: 170, y: 180 },
    { x: 320, y: 150 },
    { x: 40, y: 250 },
    { x: 160, y: 300 },
    { x: 330, y: 260 },
    { x: 230, y: 370 },
    { x: 40, y: 330 },
    { x: 350, y: 70 },
  ];

  const connections = [
    [0, 1],
    [0, 3],
    [1, 2],
    [1, 4],
    [2, 5],
    [3, 4],
    [3, 6],
    [4, 5],
    [4, 7],
    [4, 8],
    [5, 8],
    [6, 7],
    [7, 8],
    [8, 9],
    [7, 10],
    [2, 11],
  ];

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 400 400"
        preserveAspectRatio="xMidYMid slice"
        style={{ overflow: "visible" }}
      >
        <defs>
          {/* ✅ Expanded region — blur fades out naturally instead of being clipped into a square */}
          <filter id="glow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* CONNECTIONS */}
        {connections.map(([start, end], index) => (
          <motion.line
            key={`line-${index}`}
            x1={nodes[start].x}
            y1={nodes[start].y}
            x2={nodes[end].x}
            y2={nodes[end].y}
            stroke={lineColor}                        // ✅ prop
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{
              pathLength: 1,
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 2 + Math.random(),
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.1,
            }}
          />
        ))}

        {/* MOVING SIGNALS */}
        {connections.map(([start, end], index) => (
          <motion.circle
            key={`signal-${index}`}
            r="3"
            fill={signalColor}                        // ✅ prop
            filter="url(#glow)"
          >
            <animateMotion
              dur={`${1.5 + Math.random() * 2}s`}
              repeatCount="indefinite"
              path={`M${nodes[start].x},${nodes[start].y} L${nodes[end].x},${nodes[end].y}`}
            />
          </motion.circle>
        ))}

        {/* NODES */}
        {nodes.map((node, i) => (
          <motion.circle
            key={`node-${i}`}
            cx={node.x}
            cy={node.y}
            r="4"
            fill={nodeColor}                          // ✅ prop
            filter="url(#glow)"
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </svg>
    </Box>
  );
};

// ----------------------------------------------------------

const FORGOT_PASSWORD_STEPS = {
  EMAIL_INPUT: "emailInput",
  OTP_INPUT: "otpInput",
  RESET_PASSWORD: "resetPassword",
  SUCCESS: "success",
};

const SignInForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    emailAddress: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [playSuccessAnim, setPlaySuccessAnim] = useState(false);

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordCurrentStep, setForgotPasswordCurrentStep] = useState(
    FORGOT_PASSWORD_STEPS.EMAIL_INPUT
  );
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState("");
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState("");
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccessMessage, setForgotPasswordSuccessMessage] = useState("");

  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return "Please enter a valid email address";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "emailAddress") {
      setEmailError(validateEmail(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");

    const emailValidationError = validateEmail(formData.emailAddress);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    setLoading(true);

    try {
      const hashedPassword = md5(formData.password);
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE_URL}/PatientPortal/patientLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailAddress: formData.emailAddress,
          password: hashedPassword,
          pin: 0,
        }),
      });

      const raw = await response.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        result = { message: raw };
      }

      if (!response.ok || !result.token) {
        throw new Error(result.message || "Invalid credentials");
      }

      localStorage.setItem("neuro_patientportal_auth_token", result.token);
      localStorage.setItem("neuro_patientportal_userEmail", formData.emailAddress);
      localStorage.setItem("neuro_patientportal_userName", result.userName);

      setPlaySuccessAnim(true);
    } catch (err) {
      setError(err.message || "Login failed.");
      setLoading(false);
    }
  };

  const handleAnimationComplete = () => {
    if (playSuccessAnim) {
      navigate("/dashboard/Dashboard");
    }
  };

  const forgotPasswordModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: 400 },
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    outline: "none",
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.EMAIL_INPUT);
    setForgotPasswordEmail("");
    setForgotPasswordOtp("");
    setForgotPasswordNewPassword("");
    setForgotPasswordConfirmPassword("");
    setForgotPasswordLoading(false);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");
  };

  // --- API INTEGRATIONS FOR FORGOT PASSWORD FLOW ---

  // 1) Send OTP: POST /PatientPortal/forgot-password?email=...
  const handleSendOtp = async () => {
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");

    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email address.");
      setForgotPasswordLoading(false);
      return;
    }
    const emailValidationError = validateEmail(forgotPasswordEmail);
    if (emailValidationError) {
      setForgotPasswordError(emailValidationError);
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const url = `${API_BASE_URL}/PatientPortal/forgot-password?email=${encodeURIComponent(
        forgotPasswordEmail
      )}`;

      const response = await fetch(url, {
        method: "POST",
      });

      const raw = await response.text();
      let res = {};
      try {
        res = raw ? JSON.parse(raw) : {};
      } catch {
        res = { message: raw };
      }

      if (!response.ok) {
        throw new Error(res.message || "Failed to send OTP.");
      }

      setForgotPasswordSuccessMessage(res.message || "OTP sent to your email.");
      setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.OTP_INPUT);
    } catch (err) {
      setForgotPasswordError(err.message || "Failed to send OTP.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // 2) Verify OTP: POST /PatientPortal/verify-otp  body: { email, otp }
  const handleVerifyOtp = async () => {
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");

    if (!forgotPasswordOtp) {
      setForgotPasswordError("Please enter the OTP.");
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE_URL}/PatientPortal/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          otp: forgotPasswordOtp,
        }),
      });

      const raw = await response.text();
      let res = {};
      try {
        res = raw ? JSON.parse(raw) : {};
      } catch {
        res = { message: raw };
      }

      if (!response.ok) {
        throw new Error(res.message || "Invalid OTP. Please try again.");
      }

      setForgotPasswordSuccessMessage(res.message || "OTP verified.");
      setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.RESET_PASSWORD);
    } catch (err) {
      setForgotPasswordError(err.message || "Failed to verify OTP.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // 3) Reset Password: POST /PatientPortal/reset-password body: { email, newPassword, confirmPassword }
  const handleResetPassword = async () => {
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccessMessage("");

    if (forgotPasswordNewPassword.length < 6) {
      setForgotPasswordError("New password must be at least 6 characters long.");
      setForgotPasswordLoading(false);
      return;
    }
    if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
      setForgotPasswordError("Passwords do not match.");
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      // MD5 hash both password fields before sending
      const hashedNewPassword = md5(forgotPasswordNewPassword);
      const hashedConfirmPassword = md5(forgotPasswordConfirmPassword);

      const payload = {
        email: forgotPasswordEmail,
        newPassword: hashedNewPassword,
        confirmPassword: hashedConfirmPassword,
      };

      const response = await fetch(`${API_BASE_URL}/PatientPortal/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const raw = await response.text();
      let res = {};
      try {
        res = raw ? JSON.parse(raw) : {};
      } catch {
        res = { message: raw };
      }

      if (!response.ok) {
        throw new Error(res.message || "Failed to reset password. Please try again.");
      }

      setForgotPasswordSuccessMessage(res.message || "Password reset successfully!");
      setForgotPasswordCurrentStep(FORGOT_PASSWORD_STEPS.SUCCESS);
    } catch (err) {
      setForgotPasswordError(err.message || "Failed to reset password.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleForgotPassword = ()=>{
    setShowForgotPasswordModal(true)
    setForgotPasswordEmail(formData.emailAddress)
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "#FFFFFF",
        zIndex: 9999,
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* =============== 📱 MOBILE TOP CIRCLE (STATIC) WITH NEURONS =============== */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          top: "-35%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "140%",
          height: "70%",
          borderRadius: "50%",
          // ✅ TRUEVV PURPLE GRADIENT
          background: "radial-gradient(circle at 50% 80%, #7A76F6 0%, #3F3DB8 85%)",
          zIndex: 4,
          // ✅ PURPLE SHADOW
          boxShadow: "0 10px 40px rgba(86,81,244,0.35)",
          overflow: "hidden",
        }}
      >
        <MiniNeuralNetwork />

        {/* Mobile Welcome Text */}
        <Box
          sx={{
            position: "absolute",
            bottom: "6%",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            zIndex: 4,
            width: "80%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              mt: 6,
            }}
          >
            <Box
              component={motion.div}
              animate={{ rotateY: [0, 180, 360] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <img
                src="/images/newlogo.PNG"
                alt="logo"
                style={{ height: 100, width: "auto", display: "block" }}
              />
            </Box>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "white",
              mb: 1,
              mt: 2,
              display: { xs: "block", md: "none" },
            }}
          >
            Sign In
          </Typography>
        </Box>
      </Box>

      {/* =============== LEFT SIDE (Hidden on Mobile) =============== */}
      <Box
        component={motion.div}
        initial={{ x: -40, opacity: 0 }}
        animate={{
          x: playSuccessAnim ? -400 : 0,
          opacity: playSuccessAnim ? 0 : 1,
        }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
        sx={{
          display: { xs: "none", md: "flex" },
          flex: { md: "0 0 50%" },
          position: "relative",
          bgcolor: "transparent",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          pl: { md: 12 },
          pr: { md: 6 },
          overflow: "hidden",
        }}
      >
        {/* 1. MAIN BG CIRCLE */}
        <Box
          component={motion.div}
          initial={{ scale: 0.95, x: -30 }}
          animate={{
            scale: playSuccessAnim ? 1.2 : [1, 1.03, 1],
            x: playSuccessAnim ? -400 : [0, -10, 0],
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 4,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            position: "absolute",
            top: "-35%",
            left: "-28%",
            width: "120%",
            height: "120%",
            borderRadius: "50%",
            // ✅ TRUEVV PURPLE GRADIENT
            background: "radial-gradient(circle at 20% 10%, #7A76F6 0%, #3F3DB8 85%)",
            zIndex: 0,
          }}
        />

        {/* 2. BOTTOM-LEFT CIRCLE (with neurons) */}
        <Box
          component={motion.div}
          initial={{ y: 40, opacity: 0.8 }}
          animate={{
            y: playSuccessAnim ? 200 : [0, 10, 0],
            opacity: playSuccessAnim ? 0 : 1,
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 3,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            position: "absolute",
            bottom: "-5%",
            left: "-13%",
            width: "45%",
            height: "45%",
            borderRadius: "50%",
            // ✅ TRUEVV PURPLE GRADIENT
            background: "radial-gradient(circle at 20% 10%, #7A76F6 0%, #3F3DB8 85%)",
            zIndex: 1,
            boxShadow: "-10px 10px 30px rgba(0,0,0,0.2)",
            overflow: "hidden",
          }}
        >
          <MiniNeuralNetwork />
        </Box>

        {/* 3. MIDDLE CIRCLE (with neurons) */}
        <Box
          component={motion.div}
          initial={{ y: 30, opacity: 1 }}
          animate={{
            y: playSuccessAnim ? -200 : [0, -8, 0],
            opacity: playSuccessAnim ? 0.6 : 1,
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 3.5,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            position: "absolute",
            top: "60%",
            right: "20%",
            transform: "translateY(-20%)",
            width: "35%",
            height: "35%",
            borderRadius: "50%",
            // ✅ TRUEVV PURPLE GRADIENT
            background: "radial-gradient(circle at 20% 10%, #7A76F6 0%, #3F3DB8 85%)",
            zIndex: 2,
            boxShadow: "-10px 10px 30px rgba(0,0,0,0.2)",
            display: { xs: "none", md: "block" },
            overflow: "hidden",
          }}
        >
          <MiniNeuralNetwork />
        </Box>

        {/* LEFT TEXT */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -20 }}
          animate={{
            opacity: playSuccessAnim ? 0 : 1,
            x: playSuccessAnim ? -100 : 0,
          }}
          transition={{ duration: 0.8, delay: 0.2 }}
          sx={{ position: "relative", zIndex: 3, maxWidth: "480px", mb: 20 }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 1,
              letterSpacing: "1px",
              color: "#ffffff",
              fontSize: "3.5rem",
              textShadow: "0px 0px 20px rgba(0,0,0,0.5)",
            }}
          >
            WELCOME
          </Typography>
          <Typography
            sx={{
              mb: 3,
              fontWeight: 600,
              letterSpacing: "3px",
              fontSize: "14px",
              // ✅ LIGHT PURPLE SUBTITLE
              color: "#DEDCFC",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span
              style={{
                width: "10px",
                height: "10px",
                // ✅ PEACH ACCENT DOT
                background: "#FFE3DB",
                borderRadius: "50%",
                display: "inline-block",
                // ✅ PEACH GLOW
                boxShadow: "0 0 10px #FFE3DB",
              }}
            ></span>
            TruEvv Patient Portal
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.7,
              fontSize: "16px",
              fontWeight: 300,
            }}
          >
            Access your medical records, communicate with your doctors, and manage
            your health seamlessly with our secure neural network technology.
          </Typography>
        </Box>
      </Box>

      {/* =============== RIGHT SIDE (FORM) =============== */}
      <Box
        component={motion.div}
        initial={{ x: 40, opacity: 0 }}
        animate={{
          x: playSuccessAnim ? 400 : 0,
          opacity: playSuccessAnim ? 0 : 1,
        }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
        onAnimationComplete={handleAnimationComplete}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 8 },
          position: "relative",
          zIndex: 10,

        }}
      >
        {/* bottom-right circle */}
        <Box
          component={motion.div}
          animate={{
            scale: playSuccessAnim ? 1.3 : [1, 1.05, 1],
            x: playSuccessAnim ? 250 : 0,
            y: playSuccessAnim ? 150 : [0, -10, 0],
          }}
          transition={{
            duration: playSuccessAnim ? 0.8 : 4,
            ease: "easeInOut",
            repeat: playSuccessAnim ? 0 : Infinity,
            repeatType: "mirror",
          }}
          sx={{
            display: { xs: "none", sm: "block" },
            position: "absolute",
            bottom: "-80px",
            right: "-80px",
            width: "45%",
            height: "45%",
            borderRadius: "50%",
            // ✅ TRUEVV PURPLE GRADIENT
            background: "radial-gradient(circle at 20% 10%, #7A76F6 0%, #3F3DB8 85%)",
            zIndex: 0,
            boxShadow: "-10px 10px 30px rgba(0,0,0,0.2)",
            opacity: 0.9,
          }}
        />
        <MiniNeuralNetwork
          nodeColor="#5651F4"
          signalColor="#5651F4"
          lineColor="rgba(86, 81, 244, 0.35)"
        />

        {/* FORM CONTAINER */}
        <Box
          component={motion.div}
          initial={{ y: 40, opacity: 0 }}
          animate={{
            y: playSuccessAnim ? -250 : 0,
            opacity: playSuccessAnim ? 0 : 1,
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          sx={{

            width: "100%",
            maxWidth: "380px",
            position: "relative",
            zIndex: 2,
            mb: 5,
            mr: { xs: "auto", md: "60%", lg: "70%" },
            ml: { xs: "auto", md: 8, lg: 10 },
            mt: { xs: "-5%", md: "-10%", lg: "-20%" },
          }}
        >
          {/* LOGO (desktop) */}
          <Box
            sx={{
              mt: { xs: 4, md: 20 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
             
            }}
          >
            <Box
              component={motion.div}
              animate={{ rotateY: [0, 180, 360] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                component="img"
                src="/images/newlogo.PNG"
                alt="logo"
                sx={{
                  height: "13rem",
                  width: "auto",
                  display: { xs: "none", md: "block" },
                  mb:"-50px"
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              textAlign: "center",
              zIndex: 15,
              mt: { xs: 25, md: 0 },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#111827",
                mb: 1,
                display: { xs: "none", md: "block" },
              }}
            >
              Sign In
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: { xs: "#333", md: "#333" },
                fontSize: { xs: "16px", md: "20px" },
                mb: { xs: 4, md: 3 },
              }}
            >
              Enter Your{" "}
              <span
                style={{
                  // ✅ PEACH HIGHLIGHT WITH DARK TEXT (TruEvv add-button style)
                  color: "#3F3DB8",
                  padding: "0 4px",
                  borderRadius: "4px",
                }}
              >
                Credentials
              </span>{" "}
              To Access Your Account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* EMAIL FIELD (with validation) */}
            <Box sx={{ mb: { xs: 4, md: 1, lg: 3 } }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#555" }}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                name="emailAddress"
                placeholder="Enter Your Email"
                value={formData.emailAddress}
                onChange={handleChange}
                variant="outlined"
                error={!!emailError}
                helperText={emailError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    // ✅ LIGHT PURPLE INPUT BG
                    bgcolor: "#EFEFFD",
                    borderRadius: "8px",
                    height: { xs: "auto", md: "auto", lg: "55px" },
                    "& fieldset": { borderColor: "#7A76F6" },
                    "&:hover fieldset": { borderColor: "#5651F4" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5651F4",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    fontSize: "14px",
                    color: "#333",
                    px: 1,
                  },
                  "& .MuiFormHelperText-root": {
                    mt: 0.5,
                    fontSize: "11px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* PASSWORD FIELD */}
            <Box sx={{ mb: { xs: 8, md: 1, lg: 2 } }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#555" }}>
                Password
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    // ✅ LIGHT PURPLE INPUT BG
                    bgcolor: "#EFEFFD",
                    borderRadius: "8px",
                    height: { xs: "auto", md: "auto", lg: "55px" },
                    "& fieldset": { borderColor: "#7A76F6" },
                    "&:hover fieldset": { borderColor: "#5651F4" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5651F4",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    fontSize: "14px",
                    color: "#333",
                    px: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HttpsIcon sx={{ color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                                               edge="end"
                        sx={{
                          color: "#6B7280",
                          ".dark-theme &": {
                            color: "rgba(255,255,255,0.7) !important",
                          },
                        }}
                      >
                       {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mt: 1, textAlign: "right" }}>
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  // ✅ TRUEVV PURPLE LINK
                  color: "#5651F4",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "0.9rem",
                }}
                onClick={handleForgotPassword}
              >
                Forgot Password?
              </button>
            </Box>

            {/* Forgot Password Modal */}
            <Modal
              open={showForgotPasswordModal}
              onClose={handleForgotPasswordClose}
              aria-labelledby="forgot-password-modal-title"
              aria-describedby="forgot-password-modal-description"
              sx={{ zIndex: (theme) => theme.zIndex.modal + 10000 }}
            >
              <Box sx={forgotPasswordModalStyle}>
                <Typography id="forgot-password-modal-title" variant="h6" component="h2" mb={2}>
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.EMAIL_INPUT && "Forgot Password"}
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.OTP_INPUT && "Verify OTP"}
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.RESET_PASSWORD && "Reset Password"}
                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.SUCCESS && "Password Reset Successful!"}
                </Typography>
                <Box id="forgot-password-modal-description">
                  {forgotPasswordLoading && (
                    <Box display="flex" justifyContent="center" my={2}>
                      <CircularProgress />
                    </Box>
                  )}

                  {forgotPasswordError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {forgotPasswordError}
                    </Alert>
                  )}
                  {forgotPasswordSuccessMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {forgotPasswordSuccessMessage}
                    </Alert>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.EMAIL_INPUT && (
                    <>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="forgot-password-email"
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        disabled={forgotPasswordLoading}
                        helperText="Enter your registered email to receive an OTP."
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} disabled={forgotPasswordLoading} color="secondary">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSendOtp}
                          disabled={forgotPasswordLoading || !forgotPasswordEmail}
                          variant="contained"
                          sx={{ ml: 1 }}
                        >
                          {forgotPasswordLoading ? <CircularProgress size={24} /> : "Send OTP"}
                        </Button>
                      </Box>
                    </>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.OTP_INPUT && (
                    <>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="forgot-password-otp"
                        label="OTP"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordOtp}
                        onChange={(e) => setForgotPasswordOtp(e.target.value)}
                        disabled={forgotPasswordLoading}
                        helperText={`An OTP has been sent to ${forgotPasswordEmail}.`}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} disabled={forgotPasswordLoading} color="secondary">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleVerifyOtp}
                          disabled={forgotPasswordLoading || !forgotPasswordOtp}
                          variant="contained"
                          sx={{ ml: 1 }}
                        >
                          {forgotPasswordLoading ? <CircularProgress size={24} /> : "Verify OTP"}
                        </Button>
                      </Box>
                    </>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.RESET_PASSWORD && (
                    <Box>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="forgot-password-new-password"
                        label="New Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordNewPassword}
                        onChange={(e) => setForgotPasswordNewPassword(e.target.value)}
                        disabled={forgotPasswordLoading}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        margin="dense"
                        id="forgot-password-confirm-password"
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={forgotPasswordConfirmPassword}
                        onChange={(e) => setForgotPasswordConfirmPassword(e.target.value)}
                        disabled={forgotPasswordLoading}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} disabled={forgotPasswordLoading} color="secondary">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleResetPassword}
                          disabled={
                            forgotPasswordLoading || !forgotPasswordNewPassword || !forgotPasswordConfirmPassword
                          }
                          variant="contained"
                          sx={{ ml: 1 }}
                        >
                          {forgotPasswordLoading ? <CircularProgress size={24} /> : "Reset Password"}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {forgotPasswordCurrentStep === FORGOT_PASSWORD_STEPS.SUCCESS && (
                    <Box textAlign="center">
                      <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
                        Your password has been successfully reset. You can now log in with your new password.
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                        <Button onClick={handleForgotPasswordClose} variant="contained">
                          Close
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Modal>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                // ✅ TRUEVV PURPLE BUTTON
                bgcolor: "#5651F4",
                color: "#ffffff",
                py: { xs: 1.2, md: 1.2, lg: 1.8 },
                mt: { xs: 0, md: 1, lg: 3 },
                borderRadius: "30px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                mb: 4,
                // ✅ PURPLE GLOW SHADOW
                boxShadow: "0 4px 10px rgba(86, 81, 244, 0.4)",
                "&:hover": { bgcolor: "#4A47D8" },
                "&:disabled": {
                  bgcolor: "#DEDCFC",
                  color: "#ffffff",
                },
              }}
            >
              {loading ? "Please Wait" : "Sign In"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignInForm;